if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.select = this.querySelector('.cro-sticky-atc__select');
        this.submitButton = this.querySelector('.cro-sticky-atc__submit');
        this.priceEl = this.querySelector('.cro-sticky-atc__price');

        // Build variant id → option values map from the embedded JSON blob.
        // The <script type="application/json"> is rendered in sticky-atc.liquid
        // immediately before this custom element.
        const variantDataEl = document.getElementById(`sticky-atc-variants-${this.sectionId}`);
        this.variantMap = {};
        if (variantDataEl) {
          try {
            const variants = JSON.parse(variantDataEl.textContent);
            variants.forEach((v) => {
              this.variantMap[v.id] = v.options; // array of option values, e.g. ['Red', 'M']
            });
          } catch (_) {
            // malformed JSON — variantMap stays empty; onSelectChange falls through gracefully
          }
        }

        const target = document.getElementById(this.dataset.observeTarget);
        if (target) {
          this.observer = new IntersectionObserver(
            ([entry]) => this.classList.toggle('cro-sticky-atc--hidden', entry.isIntersecting),
            { rootMargin: '-1px 0px 0px 0px' }
          );
          this.observer.observe(target);
        }

        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.submitMainForm.bind(this));
        }
        if (this.select) {
          this.select.addEventListener('change', this.onSelectChange.bind(this));
        }

        // subscribe / PUB_SUB_EVENTS are globals from pubsub.js / constants.js
        // loaded unconditionally in layout/theme.liquid.
        // Payload shape (verified in product-info.js): { data: { sectionId, html, variant } }
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId || !event.data.variant) return;
          this.syncFromVariant(event.data.variant, event.data.html);
        });
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      // Bar select changed: drive the main variant-selects element so the full
      // Dawn update cycle runs (fetch → variantChange publish → syncFromVariant).
      // On single-variant PDPs there is no variant-selects — guard for null.
      onSelectChange() {
        const variantId = parseInt(this.select.value, 10);
        const optionValues = this.variantMap[variantId];

        // Find the main variant-selects for this section.
        const variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);

        if (!variantSelects || !optionValues || !optionValues.length) {
          // Fallback for single-variant PDPs or missing map data: nothing to drive.
          return;
        }

        // One .product-form__input wrapper per product option, rendered in
        // option order: a <fieldset> for button/swatch pickers or a <div>
        // wrapper holding a <select> for dropdown/swatch_dropdown pickers
        // (see snippets/product-variant-picker.liquid). Types can interleave,
        // so walk the wrappers by index instead of grouping by type.
        const optionWrappers = variantSelects.querySelectorAll('.product-form__input');
        let lastChangedInput = null;

        optionWrappers.forEach((wrapper, i) => {
          const targetValue = optionValues[i];
          if (targetValue === undefined) return;

          const dropdown = wrapper.querySelector('select');
          if (dropdown) {
            const option = Array.from(dropdown.options).find((opt) => opt.value === targetValue);
            if (!option) return;
            if (dropdown.value !== targetValue) {
              dropdown.value = targetValue;
              lastChangedInput = dropdown;
            }
            // VariantSelects.selectedOptionValues (global.js) reads the
            // `selected` ATTRIBUTE (`select option[selected]`), which setting
            // select.value does not update. updateSelectionMetadata only fixes
            // the change-event target, so sync it here for every dropdown.
            dropdown.querySelectorAll('option[selected]').forEach((opt) => opt.removeAttribute('selected'));
            option.setAttribute('selected', 'selected');
          } else {
            const radio = wrapper.querySelector(`input[type="radio"][value="${CSS.escape(targetValue)}"]`);
            if (radio && !radio.checked) {
              radio.checked = true;
              lastChangedInput = radio;
            }
          }
        });

        // Dispatch a single bubbling change event from within variant-selects so
        // VariantSelects.connectedCallback listener fires and publishes
        // optionValueSelectionChange → ProductInfo fetches and publishes variantChange.
        if (lastChangedInput) {
          lastChangedInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // URL update is handled by ProductInfo.updateURL inside the Dawn cycle.
      }

      syncFromVariant(variant, html) {
        if (this.select && this.select.value !== String(variant.id)) {
          this.select.value = String(variant.id);
        }

        // Update price from the server-rendered section HTML.
        // ProductInfo.handleUpdateProductInfo uses id="price-{sectionId}" as the
        // price container element. Inside it, visible price is in
        // .price__regular .price-item--regular (no sale) or
        // .price__sale .price-item--sale (on sale).
        if (this.priceEl && html) {
          try {
            const priceContainer = html.getElementById(`price-${this.sectionId}`);
            if (priceContainer) {
              // Check the price--on-sale class on the .price wrapper to determine
              // which price element is active, then read the appropriate one.
              const priceWrapper = priceContainer.querySelector('.price');
              const isOnSale = priceWrapper?.classList.contains('price--on-sale');
              const priceEl = isOnSale
                ? priceContainer.querySelector('.price__sale .price-item--sale')
                : priceContainer.querySelector('.price__regular .price-item--regular');
              const priceText = priceEl?.textContent?.trim();
              if (priceText) this.priceEl.textContent = priceText;
            }
          } catch (_) {
            // If html parsing fails for any reason, leave the current price as-is.
          }
        }

        if (this.submitButton) {
          this.submitButton.disabled = !variant.available;
          // window.variantStrings is defined in layout/theme.liquid — always present.
          this.submitButton.textContent = variant.available
            ? window.variantStrings.addToCart
            : window.variantStrings.soldOut;
        }
      }

      submitMainForm() {
        const mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (mainSubmit && !mainSubmit.disabled) mainSubmit.click();
      }
    }
  );
}
